// TelcoTrack - Focus trap helpers
// Shared primitives for accessible overlays (modal, mobile nav drawer, popovers) so
// keyboard/focus behavior is consistent instead of re-implemented per component.

/** Selector for elements that can receive keyboard focus (excludes disabled / hidden tab stops). */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/** Returns focusable, visible descendants of `container` in DOM order. */
export function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const candidates = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  return candidates.filter((el) => isVisible(el));
}

function isVisible(el: HTMLElement): boolean {
  // offsetParent is null for display:none (and fixed-position edge cases we don't hit here).
  return el.offsetParent !== null || el === document.activeElement;
}

/** Focuses the first focusable element inside `container`, or the container itself as a fallback. */
export function focusFirstElement(container: HTMLElement): void {
  const [first] = getFocusableElements(container);
  if (first) {
    first.focus();
  } else {
    container.focus();
  }
}

/**
 * Keeps Tab / Shift+Tab focus cycling inside `container`. Call from a (keydown) handler on the
 * overlay root. No-op for any key other than Tab.
 */
export function trapTabKey(event: KeyboardEvent, container: HTMLElement): void {
  if (event.key !== 'Tab') return;

  const focusable = getFocusableElements(container);
  if (focusable.length === 0) {
    // Nothing to focus inside — keep focus pinned on the container itself.
    event.preventDefault();
    container.focus();
    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement as HTMLElement | null;

  if (event.shiftKey) {
    if (active === first || !active || !container.contains(active)) {
      event.preventDefault();
      last.focus();
    }
  } else if (active === last || !active || !container.contains(active)) {
    event.preventDefault();
    first.focus();
  }
}

/**
 * Restores focus to `element` if it is still attached to the document (element may have been
 * removed/re-rendered while the overlay was open).
 */
export function restoreFocus(element: HTMLElement | null | undefined): void {
  if (element && document.contains(element) && typeof element.focus === 'function') {
    element.focus();
  }
}
